/**
 * One destination: where to send, and how the request is built.
 *
 * This is the shared row. Price, caps, filters and schedule are **not** here —
 * those are terms of one campaign group sending to this destination and live on
 * the link, edited under Campaigns → Delivery. A destination can be linked into
 * several groups on different terms, which is the whole point of the split
 * (plan D2), and it also means an edit on this page reaches every group that
 * routes here. The header says how many that is.
 *
 * Query params, headers and "accepted when" (how the buyer's reply is read,
 * `config.acceptance`, see AcceptanceEditor) are editable. The stored `config`
 * blob can also hold field mappings, body templates and retry settings;
 * unmodelled keys are preserved on save rather than dropped, so an
 * integration built elsewhere survives an edit here.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Breadcrumbs, Button, Card, Code, Divider,
  Grid, Group, Loader, Modal, NumberInput, Select, Stack, Switch, Table, Text,
  TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlayerPlay, IconPlus, IconTrash } from '@tabler/icons-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useDeleteDestination, useDestination, useEnums, useTestDestination,
  useUpdateDestination,
} from '../api/queries';
import { useVocab } from '../vocab';
import { AcceptanceEditor, cleanAcceptance, emptyAcceptance } from './AcceptanceEditor';
import type { Acceptance } from './AcceptanceEditor';

type Pair = { key: string; value: string };

/** Rows a delivery carries. Values interpolate `{{lead_field}}` placeholders. */
function PairEditor({
  title,
  hint,
  pairs,
  onChange,
}: {
  title: string;
  hint: string;
  pairs: Pair[];
  onChange: (next: Pair[]) => void;
}) {
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <div>
          <Text size="sm" fw={600}>{title}</Text>
          <Text size="xs" c="dimmed">{hint}</Text>
        </div>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={13} />}
          onClick={() => onChange([...pairs, { key: '', value: '' }])}
        >
          Add
        </Button>
      </Group>
      {pairs.length === 0 ? (
        <Text size="xs" c="dimmed">None.</Text>
      ) : (
        <Table withRowBorders={false} verticalSpacing={4}>
          <Table.Tbody>
            {pairs.map((p, i) => (
              <Table.Tr key={i}>
                <Table.Td w="35%">
                  <TextInput
                    size="xs"
                    placeholder="key"
                    value={p.key}
                    onChange={(e) =>
                      onChange(pairs.map((x, j) => (j === i ? { ...x, key: e.currentTarget.value } : x)))
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="{{email}} or a fixed value"
                    value={p.value}
                    onChange={(e) =>
                      onChange(pairs.map((x, j) => (j === i ? { ...x, value: e.currentTarget.value } : x)))
                    }
                  />
                </Table.Td>
                <Table.Td w={40}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`Remove ${p.key || 'row'}`}
                    onClick={() => onChange(pairs.filter((_, j) => j !== i))}
                  >
                    <IconTrash size={15} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

export function DestinationDetailPage() {
  const v = useVocab();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isPending, error } = useDestination(id);
  const { data: enums } = useEnums();
  const save = useUpdateDestination(id);
  const remove = useDeleteDestination();
  const test = useTestDestination();

  const [name, setName] = useState('');
  const [method, setMethod] = useState('POST');
  const [deliveryMethod, setDeliveryMethod] = useState('direct');
  const [endpoint, setEndpoint] = useState('');
  const [pingEndpoint, setPingEndpoint] = useState('');
  const [postEndpoint, setPostEndpoint] = useState('');
  const [timeout, setTimeoutMs] = useState<number>(30000);
  const [active, setActive] = useState(true);
  const [queryParams, setQueryParams] = useState<Pair[]>([]);
  const [headers, setHeaders] = useState<Pair[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [acceptance, setAcceptance] = useState<Acceptance>(emptyAcceptance());

  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setMethod(data.method || 'POST');
    setDeliveryMethod(data.delivery_method || 'direct');
    setEndpoint(data.endpoint_url);
    setPingEndpoint(data.ping_endpoint);
    setPostEndpoint(data.post_endpoint);
    setTimeoutMs(data.timeout_ms);
    setActive(data.active);
    // Both are `#[serde(default)]` server-side, so the generated type marks
    // them optional: a destination with no config at all omits them entirely.
    setQueryParams((data.config.query_params ?? []).map((p) => ({ ...p })));
    setHeaders((data.config.headers ?? []).map((p) => ({ ...p })));
    setAcceptance({ ...emptyAcceptance(), ...(data.config.acceptance ?? {}) });
  }, [data]);

  const isPingPost = deliveryMethod === 'ping_post' || deliveryMethod === 'ping-post';

  async function submit() {
    try {
      await save.mutateAsync({
        name,
        method,
        delivery_method: deliveryMethod,
        endpoint_url: endpoint,
        ping_endpoint: pingEndpoint,
        post_endpoint: postEndpoint,
        timeout_ms: timeout,
        active,
        config: {
          query_params: queryParams.filter((p) => p.key.trim()),
          headers: headers.filter((p) => p.key.trim()),
          // Always sent, so clearing every rule removes them (an empty
          // object) rather than leaving the stored ones in place.
          acceptance: cleanAcceptance(acceptance),
        },
      });
      notifications.show({ color: 'teal', title: `${v.One('destination')} saved`, message: name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: (e as Error).message });
    }
  }

  async function runTest() {
    try {
      const r = await test.mutateAsync({
        url: isPingPost ? pingEndpoint || endpoint : endpoint,
        method,
        timeout_ms: timeout,
        headers: headers.filter((p) => p.key.trim()),
        acceptance: cleanAcceptance(acceptance),
      });
      // How the rules above read the real reply, when it answered.
      const judged = r.verdict
        ? r.verdict.accepted
          ? ' The rules read it as accepted.'
          : ` The rules read it as rejected${r.verdict.reason ? `: ${r.verdict.reason.replace(/^Buyer /, '')}` : ''}.`
        : '';
      notifications.show({
        color: r.ok && (!r.verdict || r.verdict.accepted) ? 'teal' : 'chartAmber',
        title: r.ok ? `${v.p.url} answered ${r.status}` : `${v.p.url} did not accept the request`,
        message: (r.error ?? `${r.status ?? 'no status'} in ${r.duration_ms}ms.`) + judged,
      });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Test failed to run', message: (e as Error).message });
    }
  }

  async function doDelete() {
    if (!id) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({
        color: 'teal',
        title: `${v.One('destination')} deleted`,
        message: `${name} and its ${v.one('route')} links are gone.`,
      });
      navigate('/destinations');
    } catch (e) {
      notifications.show({ color: 'red', title: 'Delete failed', message: (e as Error).message });
    }
  }

  return (
    <Stack>
      <Breadcrumbs>
        {/* Routes › Endpoints › this one. */}
        <Anchor component={Link} to="/routes" size="sm">{v.Many('route')}</Anchor>
        <Anchor component={Link} to="/destinations" size="sm">{v.Many('destination')}</Anchor>
        <Text size="sm" c="dimmed">{data?.name ?? '…'}</Text>
      </Breadcrumbs>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}

      {data && (
        <>
          <Group gap="sm">
            <Title order={2}>{data.name}</Title>
            <Badge variant="light" color={data.active ? 'chartGreen' : 'gray'}>
              {data.active ? 'Active' : 'Paused'}
            </Badge>
            {data.buyer_name && <Badge variant="outline" color="gray">{data.buyer_name}</Badge>}
          </Group>

          {data.link_count === 0 ? (
            <Alert color="chartAmber" variant="light" title={`Not linked to any ${v.one('routeGroup')}`}>
              <Text size="sm">
                {`No ${v.one('routeGroup')} routes here, so nothing can ever be delivered to this ${v.p.urlLower}. Add it to ${v.a('routeGroup')} under ${v.Many('campaign')} → Delivery.`}
              </Text>
            </Alert>
          ) : data.link_count > 1 ? (
            <Alert color="gray" variant="light" title={`Used by ${v.count(data.link_count, 'routeGroup')}`}>
              <Text size="sm">
                {`Changes here apply everywhere this ${v.one('destination')} is used. Terms that differ per ${v.one('routeGroup')} (price, caps, filters, schedule) live on the link, not here.`}
              </Text>
            </Alert>
          ) : null}

          <Card withBorder padding="md">
            <Stack>
              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <Select
                    label="Delivery method"
                    /* Served, so this cannot offer a mode the engine lacks. */
                    data={(enums?.delivery_methods ?? []).map((m) => ({ value: m.value, label: m.label }))}
                    value={deliveryMethod}
                    allowDeselect={false}
                    onChange={(v) => v && setDeliveryMethod(v)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <Select
                    label="HTTP method"
                    data={['POST', 'GET', 'PUT'].map((m) => ({ value: m, label: m }))}
                    value={method}
                    allowDeselect={false}
                    onChange={(v) => v && setMethod(v)}
                  />
                </Grid.Col>
              </Grid>

              {isPingPost ? (
                <Grid gutter="sm">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={v.p.pingUrl}
                      description={`Asked whether the ${v.one('buyer')} wants the ${v.one('lead')}`}
                      value={pingEndpoint}
                      onChange={(e) => setPingEndpoint(e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={v.p.postUrl}
                      description={`Sent the full ${v.one('lead')} once the ping is accepted`}
                      value={postEndpoint}
                      onChange={(e) => setPostEndpoint(e.currentTarget.value)}
                    />
                  </Grid.Col>
                </Grid>
              ) : (
                <TextInput
                  label={v.p.url}
                  placeholder="https://buyer.example.com/leads"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.currentTarget.value)}
                />
              )}

              <Grid gutter="sm">
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <NumberInput
                    label="Timeout (ms)"
                    min={100}
                    step={500}
                    value={timeout}
                    onChange={(v) => setTimeoutMs(Number(v) || 30000)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 9 }}>
                  <Switch
                    mt={28}
                    label={`${v.One('destination')} is active`}
                    description={`Paused means no ${v.one('routeGroup')} will try it`}
                    checked={active}
                    onChange={(e) => setActive(e.currentTarget.checked)}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          <Card withBorder padding="md">
            <Stack>
              <Text size="sm" c="dimmed">
                Values may use <Code>{'{{field}}'}</Code> placeholders, replaced with the
                {` ${v.one('lead')}'s value at delivery: `}<Code>{'{{email}}'}</Code>, <Code>{'{{postcode}}'}</Code>,
                {` and any custom field the ${v.one('campaign')} captures.`}
              </Text>
              <PairEditor
                title="Query parameters"
                hint="Appended to the URL on every delivery."
                pairs={queryParams}
                onChange={setQueryParams}
              />
              <Divider />
              <PairEditor
                title="Headers"
                hint="Sent with every request. Auth tokens go here."
                pairs={headers}
                onChange={setHeaders}
              />
            </Stack>
          </Card>

          <AcceptanceEditor
            value={acceptance}
            onChange={setAcceptance}
            isPingPost={isPingPost}
            legacy={data.legacy_response}
          />

          <Group justify="space-between">
            <Button variant="subtle" color="red" onClick={() => setConfirmingDelete(true)}>
              {`Delete ${v.one('destination')}`}
            </Button>
            <Group>
              <Button
                variant="default"
                leftSection={<IconPlayerPlay size={15} />}
                loading={test.isPending}
                disabled={!endpoint && !pingEndpoint}
                onClick={runTest}
              >
                Test endpoint
              </Button>
              <Button loading={save.isPending} disabled={!name.trim()} onClick={submit}>
                {`Save ${v.one('destination')}`}
              </Button>
            </Group>
          </Group>

          <Text size="xs" c="dimmed">
            {`The test sends a real request to this ${v.p.urlLower} with the headers above. It does not send ${v.a('lead')}, and nothing is recorded against the ${v.one('campaign')}.`}
          </Text>

          <Modal
            opened={confirmingDelete}
            onClose={() => setConfirmingDelete(false)}
            title={`Delete ${data.name}?`}
          >
            <Stack>
              <Alert color="red" variant="light" title="This cannot be undone">
                <Text size="sm">
                  {`The ${v.one('destination')} and its ${data.link_count} ${data.link_count === 1 ? 'link' : 'links'} are removed. Its ${v.p.urlLower} configuration${
                    (data.config.query_params ?? []).length > 0
                      ? `, including ${data.config.query_params?.length} query parameters,`
                      : ''
                  } is gone and would have to be rebuilt from scratch.`}
                </Text>
              </Alert>
              {data.link_count > 0 && (
                <Text size="sm">
                  {`${data.link_count} ${data.link_count === 1 ? 'group' : 'groups'} currently deliver here. They will stop, and their ${v.many('lead')} fall through to the next tier.`}
                </Text>
              )}
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
                <Button color="red" loading={remove.isPending} onClick={doDelete}>
                  {`Delete ${v.one('destination')}`}
                </Button>
              </Group>
            </Stack>
          </Modal>
        </>
      )}
    </Stack>
  );
}
